import {
  Pagination as PaginationComponent,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from "design-system/molecules/Pagination"
import { formatNumber, isNil } from "lopermut"
import { useTranslation } from "react-i18next"

type Props = {
  currentPage: number
  totalPages: number | undefined
  onPageChange: (page: number) => void
  count: number | undefined
}

const TOTAL_PAGES = 4

export function Pagination({ currentPage, totalPages, onPageChange, count }: Props) {
  const { t } = useTranslation()

  const showPagination = totalPages !== undefined && totalPages > 1
  const showCount = !isNil(count)

  let start = Math.max(currentPage - Math.floor(TOTAL_PAGES / 2), 1)
  let end = Math.min(currentPage + Math.floor(TOTAL_PAGES / 2), totalPages ?? 0)

  if (start === 1) end = Math.min(start + TOTAL_PAGES - 1, totalPages ?? 0)
  if (end === totalPages) start = Math.max(end - TOTAL_PAGES + 1, 1)

  if (!showPagination && !showCount) return null

  return (
    <div className="flex flex-row justify-between align-middle w-full gap-4 px-2">
      {showCount && (
        <div className="flex items-center text-muted-foreground text-sm">
          {t("shared.pagination.total", { amount: formatNumber({ amount: count, fix: 0 }) })}
        </div>
      )}
      {showPagination && (
        <div className="flex flex-row flex-nowrap shrink-0 items-center justify-end gap-3">
          <span className="text-muted-foreground text-sm md:inline hidden whitespace-nowrap">
            {t("shared.pagination.page", { current: currentPage, total: totalPages })}
          </span>
          <PaginationComponent>
            <PaginationContent>
              <PaginationItem>
                <PaginationFirst onClick={() => onPageChange(1)} disabled={currentPage === 1} />
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLast
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </PaginationComponent>
        </div>
      )}
    </div>
  )
}
